import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import AppLayout from "@/components/layout/app-layout";
import { Button } from "@/components/ui/button";

// Removed Select components - status changes now only happen through workflow
import { ClaimWithDetails, Project, Organization } from "@/types";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import ClaimTimeline from "@/components/claims/claim-timeline";
import ClaimEmailDialog from "@/components/claims/claim-email-dialog";
import ClaimDetailTabs from "@/components/claims/claim-detail-tabs";
import { ClaimStatus } from "@/lib/claim-status-constants";

// Navigation context types
type ViewMode = 'active' | 'reviewing';

interface NavigationContext {
  viewMode: ViewMode;
  reviewingStage: ClaimStatus | null;
  activeStage: ClaimStatus;
}

export default function ClaimDetailPage() {
  const { claimId } = useParams();
  const [, navigate] = useLocation();
  const { toast: _toast } = useToast();
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [emailType, setEmailType] = useState<"acknowledgment" | "information-request" | "review-complete" | "approved" | "rejected">("acknowledgment");
  
  // Navigation context state
  const [navigationContext, setNavigationContext] = useState<NavigationContext>({
    viewMode: 'active',
    reviewingStage: null,
    activeStage: 'Acknowledgment' // Will be updated when data loads
  });
  
  // Fetch claim details
  const { data, isLoading, error, refetch } = useQuery<ClaimWithDetails>({
    queryKey: [`/api/claims/${claimId}`],
    enabled: !!claimId
  });
  
  // Log claim data when available
  useEffect(() => {
    if (data) {
      console.log("ClaimDetailPage - Claim Data:", data);
      
      // For testing: Check if we have status changes
      console.log("Status Changes:", data.statusChanges);
      
      // If no status changes, add test status change when component mounts
      if (data.statusChanges && data.statusChanges.length === 0) {
        console.log("Adding test status change...");
        const addTestStatusChange = async () => {
          try {
            const response = await apiRequest("POST", `/api/claims/${claimId}/statusChanges`, {
              previousStatus: "Creation",
              newStatus: data.claim.status
            });
            console.log("Added test status change:", await response.json());
            // Refresh the data
            queryClient.invalidateQueries({ queryKey: [`/api/claims/${claimId}`] });
          } catch (error) {
            console.error("Failed to add test status change:", error);
          }
        };
        
        addTestStatusChange();
      }
    }
  }, [data, claimId]);
  
  // Get project details for breadcrumb
  const { data: projectData } = useQuery<Project>({
    queryKey: [`/api/projects/${data?.claim.projectId}`],
    enabled: !!data?.claim.projectId,
  });
  
  // Get organization details for the contractor
  const { data: organizationData } = useQuery<Organization>({
    queryKey: [
      `/api/organizations/${projectData?.contractorOrganizationId}`,
    ],
    enabled: !!projectData?.contractorOrganizationId,
  });
  
  // Update active stage when data loads
  useEffect(() => {
    if (data?.claim.status) {
      setNavigationContext(prev => ({
        ...prev,
        activeStage: data.claim.status as ClaimStatus
      }));
    }
  }, [data?.claim.status]);

  // Navigation functions
  const handleStageNavigation = (stage: ClaimStatus) => {
    if (stage === navigationContext.activeStage) {
      // Return to active mode if clicking current stage
      setNavigationContext(prev => ({
        ...prev,
        viewMode: 'active',
        reviewingStage: null
      }));
    } else {
      // Enter reviewing mode for the selected stage
      setNavigationContext(prev => ({
        ...prev,
        viewMode: 'reviewing',
        reviewingStage: stage
      }));
    }
  };

  const returnToActiveStage = () => {
    setNavigationContext(prev => ({
      ...prev,
      viewMode: 'active',
      reviewingStage: null
    }));
  };

  // Get current viewing stage (either active or reviewing)
  const currentViewingStage = navigationContext.viewMode === 'reviewing' 
    ? navigationContext.reviewingStage 
    : navigationContext.activeStage;
  
  // Removed handleStatusChange - status changes now only happen through workflow
  
  // Removed handleUpdateClaim - status changes now only happen through workflow
  
  // Go back to project
  // const goToProject = () => {
  //   if (data?.claim.projectId) {
  //     navigate(`/projects/${data.claim.projectId}`);
  //   } else {
  //     navigate("/projects");
  //   }
  // };
  
  const handleClaimUpdate = () => {
    refetch();
  };

  if (error) {
    return (
      <AppLayout>
        <div className="p-6 text-center">
          <h2 className="text-xl font-semibold">Error Loading Claim</h2>
          <p className="text-muted-foreground mt-2">Failed to load claim details.</p>
          <Button className="mt-4" onClick={() => navigate("/projects")}>
            Back to Projects
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          
          {/* Claim Header */}
          <div className="mb-6">
            {isLoading ? (
              <div className="space-y-4">
                <div className="bg-muted animate-pulse rounded-md h-8 w-3/4" />
                <div className="bg-muted animate-pulse rounded-md h-5 w-64" />
                <div className="flex space-x-4">
                  <div className="bg-muted animate-pulse rounded-md h-10 w-24" />
                  <div className="bg-muted animate-pulse rounded-md h-10 w-40" />
                </div>
              </div>
            ) : data ? (
              <>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h1 className="text-2xl font-semibold">
                      Claim {data.claim.claimIdentifier || "#" + data.claim.claimId.substring(0, 8)}
                    </h1>
                    <p className="text-lg mt-1 text-neutral-600">
                      {data.claim.claimHeadline}
                    </p>
                  </div>
                  
                  {/* Removed dangerous status dropdown - status changes now only happen through workflow */}
                </div>
              </>
            ) : null}
          </div>
          
          {/* Context Navigation Header - Removed as requested by user */}

          {/* Claim Timeline - Always visible at the top */}
          {isLoading ? (
            <div className="mb-6">
              <div className="bg-muted animate-pulse rounded-md h-32 w-full" />
            </div>
          ) : data ? (
            <div className="mb-6">
              <ClaimTimeline 
                claim={data.claim}
                navigationContext={navigationContext}
                onStageNavigation={handleStageNavigation}
                onOpenEmailDialog={() => {
                  setEmailType("acknowledgment");
                  setIsEmailDialogOpen(true);
                }}
              />
            </div>
          ) : null}
          
          {/* Claim Content */}
          {isLoading ? (
            <div className="bg-muted animate-pulse rounded-md h-96 w-full" />
          ) : data ? (
            <ClaimDetailTabs 
              claimData={data}
              navigationContext={navigationContext}
              currentViewingStage={currentViewingStage}
              onOpenEmailDialog={() => {
                setEmailType(
                  data.claim.status === "Approved" ? "approved" : 
                  data.claim.status === "Rejected" ? "rejected" : 
                  "acknowledgment"
                );
                setIsEmailDialogOpen(true);
              }} 
              onClaimUpdate={handleClaimUpdate}
              onReturnToActiveStage={returnToActiveStage}
            />
          ) : null}
          
          {/* Email Dialog */}
          {data && projectData && organizationData && (
            <ClaimEmailDialog
              open={isEmailDialogOpen}
              onOpenChange={setIsEmailDialogOpen}
              claim={data.claim}
              project={projectData}
              organization={organizationData}
              emailType={emailType}
              onSent={handleClaimUpdate}
            />
          )}
        </div>
      </div>
    </AppLayout>
  );
}